import React, { useEffect } from 'react';
import './labcss/privacy.css';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';

const LAST_UPDATED = 'October 23, 2026';

const toc = [
  { id: 'scope', label: '01. Scope & Who We Are' },
  { id: 'data-we-collect', label: '02. Data We Collect' },
  { id: 'how-we-use', label: '03. How We Use Data' },
  { id: 'legal-bases', label: '04. Legal Bases (EU/UK)' },
  { id: 'sharing', label: '05. Who We Share Data With' },
  { id: 'cookies', label: '06. Cookies & Tracking' },
  { id: 'payments', label: '07. Payments' },
  { id: 'retention', label: '08. Data Retention' },
  { id: 'security', label: '09. Security' },
  { id: 'your-rights', label: '10. Your Rights (incl. India)' },
  { id: 'children', label: '11. Children' },
  { id: 'changes', label: '12. Changes' },
  { id: 'contact', label: '13. Contact & DPA' },
];

const dataPoints = [
  {
    title: 'Account & profile',
    items: ['Name', 'Email', 'Password (hashed via Firebase Auth)', 'Username / display name'],
  },
  {
    title: 'Usage & device',
    items: ['IP address', 'Browser/OS, device type', 'Pages/features used, events (analytics)'],
  },
  {
    title: 'Payments & billing',
    items: ['Billing name and email', 'Plan details and transaction IDs', 'Card/UPI details processed by PayPal (outside India) or Razorpay (India); we do not store full card data'],
  },
  {
    title: 'Support & community',
    items: ['Messages you send us', 'Posts/comments you submit'],
  },
  {
    title: 'Your SoapLab records',
    items: ['Recipes, ingredients, suppliers, batches, notes and labels', 'Sales records, including any customer names or phone numbers you choose to enter'],
  },
];

const usagePoints = [
  'Provide and secure your SoapLab account',
  'Process subscriptions and receipts',
  'Save and sync recipes, inventory, batches, and settings',
  'Improve features via analytics and error logs',
  'Send essential service emails (login, billing) and optional product updates (only with your consent)',
  'Prevent fraud, abuse, or security incidents',
  'We do not use your data to train AI models, and SoapLab does not send your data to AI services',
];

const rights = [
  'Access: request a copy of your data',
  'Correction: fix inaccurate information',
  'Deletion: delete your account and related personal data (subject to legal retention duties)',
  'Portability: receive data in a portable format where applicable',
  'Objection/Restriction: limit certain processing when allowed by law',
  'Marketing opt-out: unsubscribe from non-essential emails at any time',
];

export default function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | SoapLab';
  }, []);

  return (
    <>
      <div className="sl-page privacy-page">
        <div className="pageheader borderleft">
          <Breadcrumbs
            items={[
              { label: 'Home', to: '/' },
              { label: 'Privacy Policy' }
            ]}
          />
          <div className="pageheader-row">
            <div className="pageheading">Privacy Policy</div>
            {/* <div className="pagedescription">
              How we collect, use, share, and protect your data across SoapLab web and mobile
              experiences.
            </div> */}
          </div>
        </div>

        <div className="privacy-content borderleft">

          <p className="privacy-hero__tagline mypadding">
            We collect only what we need to run SoapLab, keep your account secure, and deliver the
            tools you choose to use. No surprise uses. No selling of personal data.
          </p>

          <div className="privacy-meta borderbottom mypadding">
            <span>Effective date: {LAST_UPDATED}</span>
            <span>Applies to soaplab.in and SoapLab mobile/desktop experiences.</span>
          </div>


          <section className="privacy-card mypaddingmoretop">
            <h2>Quick Snapshot</h2> <br />
            <div className="snapshot-grid">
              <div className="snapshot-item">
                <div className="snapshot-label">Data We Collect</div>
                <div className="snapshot-value bodytext">Name, email, password hash, billing info, IP, cookies, analytics events.</div>
              </div> 
              <div className="snapshot-item">
                <div className="snapshot-label">Why</div> 
                <div className="snapshot-value bodytext">Account access, payments, syncing your recipes, improving reliability.</div>
              </div>
              <div className="snapshot-item">
                <div className="snapshot-label">Who Handles Payments</div> 
                <div className="snapshot-value bodytext">PayPal (outside India) and Razorpay (India). We never store full card data.</div>
              </div>
              <div className="snapshot-item">
                <div className="snapshot-label">Opt-Outs</div> 
                <div className="snapshot-value bodytext">Analytics cookies and marketing emails are optional. Service emails remain.</div>
              </div>
            </div>
          </section> <br />

          <section className="privacy-toc mypadding">
            <h2>Jump to a section</h2> <br />
            <div className="toc-grid">
              {toc.map((item) => (
                <a key={item.id} href={`#${item.id}`} className="toc-link">
                  {item.label}
                </a>
              ))}
            </div>
          </section> <br /><br />

          <section id="scope" className="privacy-card mypadding">
            <h2>01. Scope &amp; Who We Are</h2> <br />
            <p className="bodytext">
              SoapLab is operated by SoapLab ("we", "us"). This Policy explains how we handle personal data when you
              use our website, calculators, forums, and subscription services.
            </p>
          </section> <br /><br />

          <section id="data-we-collect" className="privacy-card mypadding">
            <h2>02. Data We Collect</h2> <br />
            <div className="data-grid">
              {dataPoints.map((block) => (
                <div key={block.title} className="data-block">
                  <p className='smallheadingtext'>{block.title}</p>
                  <span>
                    {block.items.map((item) => (
                      <React.Fragment key={item}><span className="bodytext">{item}</span> <br/></React.Fragment>
                    ))}
                  </span>
                </div>
              ))}
            </div>
            <p className="note">
              Sources: data you provide, data created as you use features (e.g., recipe saves), and device data from cookies
              or similar technologies. If you enter details about your own customers, you decide what to record and are responsible
              for having the right to record it; we process it only to provide SoapLab to you.
            </p>
          </section> <br /><br />

          <section id="how-we-use" className="privacy-card mypadding">
            <h2>03. How We Use Data</h2> <br />
            <ul className="bullet-grid">
              {usagePoints.map((item) => (
                <li className='bodytext' key={item}>{item}</li>
              ))}
            </ul>
          </section> <br /><br />

          <section id="legal-bases" className="privacy-card mypadding">
            <h2>04. Legal Bases (EU/UK users)</h2> <br />
            <ul >
              <li className="bodytext">Consent – analytics cookies, marketing emails.</li>
              <li className="bodytext">Contract – creating your account, providing subscription features you choose.</li>
              <li className="bodytext">Legitimate interests – securing the service, preventing fraud, improving usability.</li>
              <li className="bodytext">Legal obligation – complying with tax, accounting, and law-enforcement requests.</li>
            </ul>
          </section> <br /> <br />

          <section id="sharing" className="privacy-card mypadding">
            <h2>05. When We Share Data</h2> <br />
            <ul>
              <li className="bodytext">Google Firebase (Google Cloud): sign-in, database, file storage and backend functions.</li>
              <li className="bodytext">Website hosting and content delivery providers that serve the site to your browser.</li>
              <li className="bodytext">Resend: delivery of account, billing, support and service emails.</li>
              <li className="bodytext">Payments: PayPal (customers outside India) and Razorpay (customers in India) process payments; they receive billing details and card/UPI data.</li>
              <li className="bodytext">Analytics: Google Analytics for usage insights; IPs are used for approximate location and device stats.</li>
              <li className="bodytext">Other users: anything you choose to make public (community posts and replies, shared recipes, your public profile and username) can be seen by anyone.</li>
              <li className="bodytext">Legal and safety: if required by law or to protect users, our platform, or the public.</li>
            </ul>
            <p className="note">We do <strong>not</strong> sell personal data, and we do not share it with AI services.</p>
            <p className="note">Our providers may store or process data outside your country, including in the United States. We use providers that protect data with industry-standard safeguards.</p>
          </section> <br /> <br />

          <section id="cookies" className="privacy-card mypadding">
            <h2>06. Cookies &amp; Tracking</h2> <br />
            <ul>
              <li className="bodytext">Essential: login sessions, load balancing, feature preferences.</li>
              <li className="bodytext">Analytics: Google Analytics cookies to understand usage. You can opt out via your browser or GA opt-out tools.</li>
              <li className="bodytext">Advertising: we currently do not run third-party ads inside the app.</li>
            </ul>
          </section><br /><br />

          <section id="payments" className="privacy-card mypadding">
            <h2>07. Payments</h2> <br />
            <p className="bodytext">
              We use PayPal for customers outside India and Razorpay for customers in India. Payment processors receive your billing
              details and handle card/UPI data directly. We store only payment status, plan, and transaction references for receipts
              and support.
            </p>
          </section><br /><br />

          <section id="retention" className="privacy-card mypadding">
            <h2>08. Data Retention</h2> <br />
            <ul>
              <li className="bodytext">Account data: kept while your account is active.</li>
              <li className="bodytext">Backups/logs: short-term operational backups and security logs are kept for a limited time then deleted.</li>
              <li className="bodytext">Billing records: retained as required for tax and accounting obligations.</li>
              <li className="bodytext">Delete requests: when you request deletion, we remove or de-identify personal data unless retention is legally required.</li>
            </ul>
          </section> <br /><br />

          <section id="security" className="privacy-card mypadding">
            <h2>09. Security</h2> <br />
            <ul>
              <li className="bodytext">Passwords are handled and hashed by Firebase Authentication; we never see them.</li>
              <li className="bodytext">Encryption in transit (HTTPS) and at rest for stored data where supported by our providers.</li>
              <li className="bodytext">Role-based access controls for admin tools.</li>
              <li className="bodytext">Best-effort monitoring for abuse and suspicious activity.</li>
            </ul>
            <p className="note">No system is perfectly secure. If you suspect an issue, contact us immediately through the <a href="/contact">Contact form</a>.</p>
          </section> <br /><br />

          <section id="your-rights" className="privacy-card mypadding">
            <h2>10. Your Rights</h2> <br />
            <ul className="bullet-grid">
              {rights.map((item) => (
                <li className="bodytext" key={item}>{item}</li>
              ))}
            </ul>
            <p className="note">
              India: under the Digital Personal Data Protection Act, 2023, you can ask for a summary of the personal data we process,
              and to correct, complete, update or erase it. You can withdraw consent at any time (this does not affect processing
              already done), nominate someone to exercise your rights if you die or become unable to, and raise a grievance with us.
              Send grievances through the Contact form marked “Privacy grievance”; we will respond within the time required by law.
              If you are not satisfied, you may complain to the Data Protection Board of India.
            </p>
            <p className="note">
              California (CCPA/CPRA) users: you may ask for access, deletion, and to opt out of “sale” or “sharing” of personal
              information. We do not sell personal information. Use the contact methods below to exercise rights.
            </p>
          </section> <br /> <br />

          <section id="children" className="privacy-card mypadding">
            <h2>11. Children</h2> <br />
            <p className="bodytext">SoapLab is for adults aged 18 and over, because soapmaking involves hazardous materials. We do not knowingly collect personal data from anyone under 18. If you believe a child has created an account, contact us and we will delete it.</p>
          </section> <br /><br />

          <section id="changes" className="privacy-card mypadding">
            <h2>12. Changes</h2> <br />
            <p className="bodytext">
              We may update this Policy to reflect changes in features or law. We will revise the "Effective date" and, if changes are
              significant, notify you via the app or email.
            </p>
          </section> <br /><br />

          <section id="contact" className="privacy-card contactcard mypadding">
            <h2>13. Contact &amp; Data Protection</h2> <br />
            <p className="bodytext">If you have questions, want to exercise your privacy rights, or need a DPA signed, use the Contact form inside SoapLab.</p> <br />
            <div className="contact-columns">
              <div>
                <p className='smallheadingtext'>Contact Form</p>
                <a href="/contact"> <p className='bodytext'>soaplab.in/contact</p></a>
              </div>
              <div> <br />
                <p className='smallheadingtext' >Mailing</p>
                <p className="bodytext">SoapLab Privacy Team<br />Request our current mailing address via the Contact form.</p>
              </div>
            </div> <br />
            <p className="note">
              EEA/UK users: you may also lodge a complaint with your local supervisory authority. We will cooperate with authorities and
              respond to verified DPA requests promptly.
            </p>
            <p className="note">We aim to reply within 7 business days.</p>
          </section> 
        </div>
        <Footer />
      </div>

    </>
  );
}
